import React, { useState, useCallback } from 'react';
import { PlusIcon, TrashIcon, ClipboardDocumentListIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const QuickOrderForm = ({ onClose }) => {
  const [orderRows, setOrderRows] = useState([
    { id: 1, sku: '', quantity: 1, product: null, loading: false, error: null }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const { addToCart } = useCart();
  const { user } = useAuth();

  // Add new row
  const addRow = () => {
    const newId = Math.max(...orderRows.map(row => row.id)) + 1;
    setOrderRows([...orderRows, { 
      id: newId, 
      sku: '', 
      quantity: 1, 
      product: null, 
      loading: false, 
      error: null 
    }]);
  };

  // Remove row
  const removeRow = (id) => {
    if (orderRows.length > 1) {
      setOrderRows(orderRows.filter(row => row.id !== id));
    }
  };

  // Update row data
  const updateRow = (id, field, value) => {
    setOrderRows(orderRows.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  // Look up product by SKU
  const lookupProductBySKU = useCallback(async (id, sku) => {
    if (!sku.trim()) return;

    updateRow(id, 'loading', true);
    updateRow(id, 'error', null);
    updateRow(id, 'product', null);

    try {
      const response = await axios.get(`/api/products/search?q=${encodeURIComponent(sku)}&searchFields=sku`);
      const products = response.data.products || [];
      const product = products.find(p => p.sku.toLowerCase() === sku.toLowerCase());
      
      if (product) {
        updateRow(id, 'product', product);
        updateRow(id, 'error', null);
      } else {
        updateRow(id, 'product', null);
        updateRow(id, 'error', `SKU "${sku}" לא נמצא`);
      }
    } catch (error) {
      console.error('Error looking up SKU:', error);
      updateRow(id, 'product', null);
      updateRow(id, 'error', 'שגיאה בחיפוש המוצר');
    } finally {
      updateRow(id, 'loading', false);
    }
  }, []);

  // Handle SKU input blur
  const handleSKUBlur = (id, sku) => {
    if (sku.trim()) {
      lookupProductBySKU(id, sku.trim());
    }
  };

  // Handle SKU input change
  const handleSKUChange = (id, value) => {
    updateRow(id, 'sku', value);
    if (!value.trim()) {
      updateRow(id, 'product', null);
      updateRow(id, 'error', null);
    }
  };

  // Parse multi-line SKU input (for pasting multiple SKUs)
  const handleBulkSKUInput = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length <= 1) return;

    const newRows = lines.map((line, index) => {
      const parts = line.trim().split(/\s+/);
      const sku = parts[0];
      const quantity = parts[1] ? parseInt(parts[1]) || 1 : 1;
      
      return {
        id: Math.max(...orderRows.map(row => row.id)) + index + 1,
        sku,
        quantity,
        product: null,
        loading: false,
        error: null
      };
    });

    setOrderRows(newRows);
    
    // Lookup all SKUs
    newRows.forEach(row => {
      if (row.sku) {
        lookupProductBySKU(row.id, row.sku);
      }
    });
  };

  // Add all valid products to cart
  const addAllToCart = async () => {
    const validRows = orderRows.filter(row => row.product && row.quantity > 0);
    
    if (validRows.length === 0) {
      setSubmitMessage({ type: 'error', text: 'אין מוצרים תקינים להוסיף' });
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const row of validRows) {
        try {
          // Calculate contract pricing for this product and quantity
          const params = new URLSearchParams();
          params.append('quantity', row.quantity.toString());
          if (user?.company_id) {
            params.append('company_id', user.company_id);
          }

          const response = await axios.get(`/api/contract-pricing/calculate/${row.product.id}?${params}`);
          const pricing = response.data.pricing;
          
          // Create product object with calculated B2B price
          const productWithB2BPrice = {
            ...row.product,
            price: pricing.final_price || pricing.unit_price || row.product.price_b2b || row.product.price_b2c,
            original_price: row.product.price_b2b || row.product.price_b2c,
            contract_discount: pricing.discount_percentage || 0,
            contract_terms: pricing.contract_terms
          };

          await addToCart(productWithB2BPrice, row.quantity);
          successCount++;
        } catch (error) {
          console.error('Error adding to cart:', error);
          // Fallback to B2B price if contract pricing fails
          try {
            const productWithB2BPrice = {
              ...row.product,
              price: row.product.price_b2b || row.product.price_b2c
            };
            await addToCart(productWithB2BPrice, row.quantity);
            successCount++;
          } catch (fallbackError) {
            console.error('Error adding to cart with fallback:', fallbackError);
            errorCount++;
          }
        }
      }

      if (successCount > 0) {
        setSubmitMessage({ 
          type: 'success', 
          text: `${successCount} מוצרים נוספו לעגלה בהצלחה${errorCount > 0 ? ` (${errorCount} נכשלו)` : ''}`
        });
        
        // Clear the form after 2 seconds
        setTimeout(() => {
          setOrderRows([{ id: 1, sku: '', quantity: 1, product: null, loading: false, error: null }]);
          setSubmitMessage(null);
        }, 2000);
      } else {
        setSubmitMessage({ type: 'error', text: 'שגיאה בהוספת המוצרים לעגלה' });
      }
    } catch (error) {
      console.error('Error in bulk add to cart:', error);
      setSubmitMessage({ type: 'error', text: 'שגיאה בהוספת המוצרים לעגלה' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validRowsCount = orderRows.filter(row => row.product && row.quantity > 0).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center text-white">
              <ClipboardDocumentListIcon className="w-6 h-6 ml-3" />
              <h2 className="text-xl font-bold">הזמנה מהירה</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="px-6 py-4 bg-blue-50 border-b">
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">איך להשתמש:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>הזן קוד מוצר (SKU) וכמות עבור כל פריט</li>
              <li>ניתן להדביק מספר שורות בפורמט: קוד מוצר [רווח] כמות</li>
              <li>לחץ על "הוסף שורה" להוספת פריטים נוספים</li>
              <li>מוצרים תקינים יוצגו עם פרטים ומחיר</li>
            </ul>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto overscroll-contain flex-1">
          {/* Bulk paste area */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              הדבק מספר קודי מוצר (אופציונלי):
            </label>
            <textarea
              placeholder="הדבק מספר שורות: קוד מוצר [רווח] כמות&#10;לדוגמה:&#10;PER001 2&#10;PER002 1&#10;PER003 3"
              className="w-full p-3 border border-gray-300 rounded-lg text-sm"
              rows="3"
              onPaste={(e) => {
                setTimeout(() => {
                  const text = e.target.value;
                  if (text.includes('\n')) {
                    handleBulkSKUInput(text);
                    e.target.value = '';
                  }
                }, 10);
              }}
            />
          </div>

          {/* Order rows */}
          <div className="space-y-3">
            <div className="grid grid-cols-12 gap-3 text-sm font-medium text-gray-700 border-b pb-2">
              <div className="col-span-3">קוד מוצר (SKU)</div>
              <div className="col-span-4">פרטי מוצר</div>
              <div className="col-span-2">מחיר ליח'</div>
              <div className="col-span-2">כמות</div>
              <div className="col-span-1">פעולות</div>
            </div>

            {orderRows.map((row) => (
              <div key={row.id} className="grid grid-cols-12 gap-3 items-center py-2 border-b border-gray-100">
                {/* SKU Input */}
                <div className="col-span-3">
                  <input
                    type="text"
                    value={row.sku}
                    onChange={(e) => handleSKUChange(row.id, e.target.value)}
                    onBlur={(e) => handleSKUBlur(row.id, e.target.value)}
                    placeholder="הזן קוד מוצר"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    disabled={row.loading}
                  />
                </div>

                {/* Product Info */}
                <div className="col-span-4">
                  {row.loading ? (
                    <div className="text-sm text-blue-600">מחפש מוצר...</div>
                  ) : row.error ? (
                    <div className="text-sm text-red-600">{row.error}</div>
                  ) : row.product ? (
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{row.product.name}</div>
                      <div className="text-gray-500">
                        {row.product.brand && `${row.product.brand} • `}
                        מלאי: {row.product.stock_quantity}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">הזן קוד מוצר</div>
                  )}
                </div>

                {/* Price */}
                <div className="col-span-2 text-sm">
                  {row.product ? (
                    <span className="font-medium text-green-600">
                      ₪{row.product.price_b2b?.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </div>

                {/* Quantity */}
                <div className="col-span-2">
                  <input
                    type="number"
                    min="1"
                    value={row.quantity}
                    onChange={(e) => updateRow(row.id, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    disabled={!row.product}
                  />
                </div>

                {/* Actions */}
                <div className="col-span-1">
                  <button
                    onClick={() => removeRow(row.id)}
                    disabled={orderRows.length === 1}
                    className="p-2 text-red-500 hover:text-red-700 disabled:text-gray-400 hover:bg-red-50 rounded-full transition-colors"
                    title="הסר שורה"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Add Row Button */}
          <button
            onClick={addRow}
            className="mt-4 flex items-center justify-center sm:justify-start w-full sm:w-auto px-4 py-3 sm:py-2 text-blue-600 hover:text-blue-700 font-medium bg-blue-50 sm:bg-transparent rounded-lg hover:bg-blue-100 sm:hover:bg-transparent transition-colors"
          >
            <PlusIcon className="w-5 h-5 ml-2" />
            הוסף שורה
          </button>

          {/* Submit Message */}
          {submitMessage && (
            <div className={`mt-4 p-3 rounded-lg ${
              submitMessage.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {submitMessage.text}
            </div>
          )}
        </div>

        {/* Footer - Sticky */}
        <div className="px-6 py-4 bg-gray-50 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            {validRowsCount > 0 ? (
              <span>{validRowsCount} מוצרים מוכנים להוספה</span>
            ) : (
              <span>הזן קודי מוצר כדי להתחיל</span>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              ביטול
            </button>
            <button
              onClick={addAllToCart}
              disabled={validRowsCount === 0 || isSubmitting}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              <ShoppingCartIcon className="w-4 h-4 ml-2" />
              {isSubmitting ? 'מוסיף...' : `הוסף לעגלה (${validRowsCount})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickOrderForm;
